import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  LinearProgress,
  CircularProgress,
  Alert,
  Chip,
  Stack,
  Divider,
  Button,
} from '@mui/material';
import {
  School as SchoolIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Person as PersonIcon,
  Add as AddIcon,
  Event as EventIcon,
} from '@mui/icons-material';
import { OnboardingUserProgress } from '../../../types/onboarding.types';
import { apiService } from '../../../services/api';
import UserOnboardingModal from './UserOnboardingModal';
import AddUserModal from './AddUserModal';
import AddUserWithTemplateModal from './AddUserWithTemplateModal';

// Inline OnboardingUserCard component
const OnboardingUserCard: React.FC<{ 
  userProgress: OnboardingUserProgress;
  onClick: () => void;
}> = ({ userProgress, onClick }) => {
  const getProgressColor = (percentage: number): 'success' | 'info' | 'warning' | 'error' => {
    if (percentage === 100) return 'success';
    if (percentage >= 80) return 'info';
    if (percentage >= 50) return 'warning';
    return 'error';
  };

  const getStatusIcon = (percentage: number) => {
    if (percentage === 100) return <CheckCircleIcon color="success" sx={{ fontSize: 20 }} />;
    if (percentage > 0) return <ScheduleIcon color="warning" sx={{ fontSize: 20 }} />;
    return <ScheduleIcon color="error" sx={{ fontSize: 20 }} />;
  };

  const getStatusText = (percentage: number) => {
    if (percentage === 100) return 'Completed';
    if (percentage > 0) return 'In Progress';
    return 'Not Started';
  };

  return (
    <Card 
      sx={{ 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        cursor: 'pointer',
        '&:hover': {
          boxShadow: 4,
          transform: 'translateY(-2px)',
        },
        transition: 'all 0.2s ease-in-out',
      }}
      onClick={onClick}
    >
      <CardContent sx={{ flexGrow: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <PersonIcon color="primary" />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            {userProgress.firstName} {userProgress.lastName} Onboarding
          </Typography>
        </Box>

        <Stack spacing={2}>
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Progress
              </Typography>
              <Typography variant="h6" color={`${getProgressColor(userProgress.completionPercentage)}.main`}>
                {userProgress.completionPercentage}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={userProgress.completionPercentage}
              color={getProgressColor(userProgress.completionPercentage)}
              sx={{ height: 8, borderRadius: 4 }}
            />
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Chip
              icon={getStatusIcon(userProgress.completionPercentage)}
              label={getStatusText(userProgress.completionPercentage)}
              color={getProgressColor(userProgress.completionPercentage)}
              variant="outlined"
              size="small"
            />
            <Typography variant="body2" color="text.secondary">
              {userProgress.completedSessions}/{userProgress.totalSessions} sessions
            </Typography>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};

const StaffOnboarding: React.FC = () => {
  const [usersProgress, setUsersProgress] = useState<OnboardingUserProgress[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<OnboardingUserProgress | null>(null);
  const [addUserModalOpen, setAddUserModalOpen] = useState(false);
  const [addUserWithTemplateModalOpen, setAddUserWithTemplateModalOpen] = useState(false);

  useEffect(() => {
    loadUsersProgress();
  }, []);

  const loadUsersProgress = async () => {
    try {
      setLoading(true);
      const data = await apiService.getOnboardingUsersProgress();
      setUsersProgress(data);
      setError(null);
    } catch (err) {
      console.error('Error loading onboarding progress:', err);
      setError('Failed to load onboarding progress');
    } finally {
      setLoading(false);
    }
  };

  const handleUserClick = (userProgress: OnboardingUserProgress) => {
    setSelectedUser(userProgress);
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedUser(null);
    // Refresh the progress data when modal closes to reflect any updates
    loadUsersProgress();
  };

  const handleAddUser = () => {
    setAddUserModalOpen(true);
  };

  const handleAddUserModalClose = () => {
    setAddUserModalOpen(false);
  };

  const handleUserAdded = () => {
    // Refresh the progress data when a user is added
    loadUsersProgress();
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">{error}</Alert>
    );
  }

  return (
    <Box>
      {/* Header with Action Buttons */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" component="h1">
            Staff Onboarding Management
          </Typography>
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddUser}
              sx={{ minWidth: 140 }}
            >
              Add User
            </Button>
            <Button
              variant="contained"
              startIcon={<EventIcon />}
              onClick={() => setAddUserWithTemplateModalOpen(true)}
              sx={{ minWidth: 180 }}
            >
              Add with Template
            </Button>
          </Stack>
        </Box>
      </Box>

      {/* Summary Statistics */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Overview
        </Typography>
        <Divider sx={{ mb: 2 }} />
        
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' }, 
          gap: 2,
          mb: 3
        }}>
          <Card sx={{ flex: 1 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <SchoolIcon color="primary" sx={{ fontSize: 40 }} />
                <Box>
                  <Typography variant="h4">{usersProgress.length}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Employees
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
          
          <Card sx={{ flex: 1 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <CheckCircleIcon color="success" sx={{ fontSize: 40 }} />
                <Box>
                  <Typography variant="h4">
                    {usersProgress.filter(u => u.completionPercentage === 100).length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Completed
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
          
          <Card sx={{ flex: 1 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <ScheduleIcon color="warning" sx={{ fontSize: 40 }} />
                <Box>
                  <Typography variant="h4">
                    {usersProgress.filter(u => u.completionPercentage > 0 && u.completionPercentage < 100).length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    In Progress
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
          
          <Card sx={{ flex: 1 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <ScheduleIcon color="error" sx={{ fontSize: 40 }} />
                <Box>
                  <Typography variant="h4">
                    {usersProgress.filter(u => u.completionPercentage === 0).length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Not Started
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Onboarding Progress List */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Employee Onboarding Progress
        </Typography>
        <Divider sx={{ mb: 2 }} />
        
        {usersProgress.length === 0 ? (
          <Alert severity="info">
            No onboarding data available. Employees will appear here once onboarding sessions are created.
          </Alert>
        ) : (
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { 
              xs: '1fr', 
              sm: 'repeat(2, 1fr)', 
              md: 'repeat(3, 1fr)' 
            }, 
            gap: 2 
          }}>
            {usersProgress.map((userProgress) => (
              <OnboardingUserCard 
                key={userProgress.userId} 
                userProgress={userProgress} 
                onClick={() => handleUserClick(userProgress)}
              />
            ))}
          </Box>
        )}
      </Box>

      {/* User Onboarding Modal */}
      <UserOnboardingModal
        open={modalOpen}
        onClose={handleModalClose}
        userProgress={selectedUser}
      />

      {/* Add User Modal */}
      <AddUserModal
        open={addUserModalOpen}
        onClose={handleAddUserModalClose}
        onUserAdded={handleUserAdded}
      />

      {/* Add User with Template Modal */}
      <AddUserWithTemplateModal
        open={addUserWithTemplateModalOpen}
        onClose={() => setAddUserWithTemplateModalOpen(false)}
        onUserAdded={handleUserAdded}
      />
    </Box>
  );
};

export default StaffOnboarding;
